/**
 * LogDetailCell — renders the `log_info` blob written by the ADT log helpers
 * in serverless/mysql_transaction.py. See docs/ADT_LOGGING_PLAN.md §4.2.
 *
 * Shape (all keys optional except v):
 *   {"v":1,
 *    "subject":{"type":"device","id":123,"name":"Pendant 42","sn":"AC91A1A38FFA"},
 *    "changes":[{"f":"resident_phone","l":"Phone","o":"414-555-1234","n":"414-555-9999"}],
 *    "trunc":3}
 *
 * `sn` is a device's manufacture_uuid. Two pull cords in one room share a
 * name, so the serial is what says which one moved. Absent on non-devices and
 * on rows written before it was captured.
 *
 * Every historical row has log_info NULL, and rows written by call sites not
 * yet migrated still will, so the empty path is the common path — it must be
 * quiet and it must never throw. Anything unparseable degrades to the same
 * dash as empty rather than surfacing a React error on a profile page.
 */

import { useState, useMemo, useCallback } from 'react';

/** Parse defensively. Returns null for anything we can't render. */
export function parseLogInfo(raw) {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    const changes = Array.isArray(parsed.changes) ? parsed.changes : [];
    const subject = parsed.subject && typeof parsed.subject === 'object' ? parsed.subject : null;
    if (!subject && changes.length === 0) return null;
    return { subject, changes, trunc: Number(parsed.trunc) || 0 };
  } catch {
    return null;
  }
}

/** Empty old/new values read as "not set" rather than as a missing value. */
const shown = (v) => (v === null || v === undefined || v === '' ? '(none)' : String(v));

/** Flat text for searching and filtering, so those cover the Details column. */
export function logInfoText(raw, fallback = '') {
  const info = parseLogInfo(raw);
  if (!info) return fallback || '';
  const parts = [];
  if (info.subject?.name) parts.push(info.subject.name);
  // Searching/filtering a serial is exactly how someone finds one of two
  // identically named pull cords.
  if (info.subject?.sn) parts.push(info.subject.sn);
  info.changes.forEach((c) => {
    parts.push(c.l || c.f || '', shown(c.o), shown(c.n));
  });
  return parts.filter(Boolean).join(' ');
}

const LogDetailCell = ({ info, fallback }) => {
  const [open, setOpen] = useState(false);
  const parsed = useMemo(() => parseLogInfo(info), [info]);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  /* No structured detail: fall back to whatever the row already showed. The
     Devices tab passes `source`, which is where suppression windows live until
     that call site is migrated. */
  if (!parsed) {
    return (
      <span className="dp-cell-text" style={{ whiteSpace: 'break-spaces' }}>
        {fallback || '—'}
      </span>
    );
  }

  const { subject, changes, trunc } = parsed;
  const one = changes.length === 1 ? changes[0] : null;

  const Change = ({ c }) => (
    <>
      <span style={{ color: 'var(--color-text-tertiary)' }}>{c.l || c.f}: </span>
      <span>{shown(c.o)}</span>
      <span style={{ color: 'var(--color-text-tertiary)' }}> → </span>
      <span>{shown(c.n)}</span>
    </>
  );

  return (
    <span className="dp-cell-text" style={{ whiteSpace: 'break-spaces' }}>
      {subject ? (
        <span>
          {subject.name || subject.id}
          {subject.sn ? (
            <span style={{
              color: 'var(--color-text-tertiary)',
              fontFamily: 'monospace',
              fontSize: 'var(--font-size-xs)',
            }}>
              {' '}({subject.sn})
            </span>
          ) : null}
        </span>
      ) : null}

      {subject && changes.length ? <span>{' · '}</span> : null}

      {/* One change reads better inline than behind a disclosure. */}
      {one ? <Change c={one} /> : null}

      {changes.length > 1 ? (
        <>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            /* background:none is load-bearing — the bare `button:hover` in
               public/assets/css/custom.css fills unstyled buttons with primary
               blue. Same trap as .rp-filter__action-btn:hover. */
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
              color: 'var(--color-primary)',
            }}
          >
            {changes.length} fields changed {open ? '▴' : '▾'}
          </button>
          {open ? (
            <span style={{ display: 'block', marginTop: 'var(--space-1)' }}>
              {changes.map((c, i) => (
                <span key={`${c.f || i}`} style={{ display: 'block' }}>
                  <Change c={c} />
                </span>
              ))}
            </span>
          ) : null}
        </>
      ) : null}

      {trunc > 0 ? (
        <span style={{ color: 'var(--color-text-tertiary)' }}> +{trunc} more</span>
      ) : null}
    </span>
  );
};

export default LogDetailCell;
